// src/pages/admin/Page_InstitutionDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Table,
  List,
  Avatar,
  Statistic,
  message,
  Skeleton,
  Divider,
  Typography,
} from "antd";
import { PageHeader } from "../../../ui";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { dashboardApi } from "../../../api/dashboardAPI";

function InfoPair({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: "20px" }}>
      <div style={{ color: "#6b7280", minWidth: 120 }}>{label}:</div>
      <div style={{ fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );
}

export function Page_InstitutionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await dashboardApi.getInstitutionDetail(accessToken, id);
        if (alive) setPayload(data);
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "No se pudo cargar el detalle";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, id]);

  // Normalizaciones y fallbacks (evita campos vacíos/variantes)
  const partner = payload?.partner || {};
  const institution = payload?.institution || {};
  const teachers = payload?.teachers || [];
  const studentsNames = payload?.studentsNames || [];
  const totals = payload?.totals || {};

  const pName = partner.nombre || partner.name || "";
  const pEmail = partner.email || partner.mail || "";
  const pAvatar =
    partner.avatar && partner.avatar !== "null" ? partner.avatar : undefined;

  const iName = institution.nombre || institution.name || "";
  const iDir = institution.direccion || institution.address || "";
  const iTel = institution.telefono || institution.phone || "";
  const iEmail = institution.email || "";
  const iAvatar =
    institution.avatar && institution.avatar !== "null"
      ? institution.avatar
      : undefined;
  const plan = institution.planEduca || {};

  const statusTag = useMemo(() => {
    const exp = institution?.expired_at ? dayjs(institution.expired_at) : null;
    const active = exp ? dayjs().isBefore(exp) : true;
    return (
      <Tag color={active ? "green" : "red"}>
        {active ? "Activa" : "Expirada"}
      </Tag>
    );
  }, [institution?.expired_at]);

  const teacherColumns = [
    {
      title: "Profesor/a",
      dataIndex: "nombre",
      key: "nombre",
      render: (v, row) => (
        <Space orientation="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{v || "—"}</span>
          <span style={{ color: "#6b7280", fontSize: 12 }}>
            {row.email || "—"}
          </span>
        </Space>
      ),
    },
    {
      title: "Roles",
      key: "roles",
      render: (_, row) =>
        Array.isArray(row.roles) && row.roles.length ? (
          <Space size={[4, 4]} wrap>
            {row.roles.map((r, idx) => (
              <Tag key={idx}>
                {r.role}
                {r.classnumber ? ` · ${r.classnumber}` : ""}
              </Tag>
            ))}
          </Space>
        ) : (
          "—"
        ),
    },
    {
      title: "Rutas didácticas",
      dataIndex: ["rutasDidacticas", "count"],
      align: "right",
      width: 150,
      render: (v) => v ?? 0,
    },
  ];

  return (
    <div className="page-dashboard">
      <PageHeader
        title="Institucion"
        subtitle={
          <span style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "#2563eb",
                cursor: "pointer",
              }}
              aria-label="Volver"
            >
              {"←"} Volver
            </button>
            <span style={{ color: "#9ca3af" }}>|</span>
            <Link to="/admin/dashboard/institutions">
              Todas las instituciones
            </Link>
          </span>
        }
      />

      {!!error && <div className="ui negative message">{error}</div>}

      {/* ======== Cabeceras ======== */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            bordered
            hoverable
            style={{ borderRadius: 10 }}
            loading={loading}
          >
            <Space align="start" size={16}>
              <Avatar
                src={pAvatar}
                alt={pName || "Partner"}
                size={48}
                style={{ background: "#e5e7eb", fontWeight: 700 }}
              >
                {(!pAvatar && (pName || "?")[0]) || null}
              </Avatar>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Partner</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {pName || "—"}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  {pEmail || "—"}
                </div>
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            bordered
            hoverable
            style={{ borderRadius: 10 }}
            loading={loading}
          >
            <Space align="start" size={16}>
              <Avatar
                src={iAvatar}
                alt={iName || "Institución"}
                size={48}
                style={{ background: "#e5e7eb", fontWeight: 700 }}
              >
                {(!iAvatar && (iName || "?")[0]) || null}
              </Avatar>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Institución
                </div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {iName || "—"} {statusTag}
                </div>
                <Space size={16} wrap>
                  <InfoPair label="Plan" value={plan?.title} />
                  <InfoPair
                    label="Cupos"
                    value={
                      plan?.teacherNumber != null &&
                      plan?.studentsNumber != null
                        ? `${plan.teacherNumber} docentes / ${plan.studentsNumber} alumnos`
                        : "—"
                    }
                  />
                </Space>
              </div>
            </Space>
            <Divider />
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <InfoPair label="Dirección" value={iDir} />
                <InfoPair label="Teléfono" value={iTel} />
                <InfoPair label="Email" value={iEmail} />
              </Col>
              <Col xs={24} sm={12}>
                <InfoPair
                  label="Fecha alta"
                  value={
                    institution.created_at
                      ? dayjs(institution.created_at).format("DD/MM/YYYY")
                      : "—"
                  }
                />
                <InfoPair
                  label="Expira"
                  value={
                    institution.expired_at
                      ? dayjs(institution.expired_at).format("DD/MM/YYYY")
                      : "—"
                  }
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ======== KPIs Totales ======== */}
      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col xs={24} sm={8}>
          <Card
            bordered
            hoverable
            style={{ borderRadius: 10 }}
            loading={loading}
          >
            <Statistic title="Profesores" value={totals?.teachers ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            bordered
            hoverable
            style={{ borderRadius: 10 }}
            loading={loading}
          >
            <Statistic title="Alumnos" value={totals?.students ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            bordered
            hoverable
            style={{ borderRadius: 10 }}
            loading={loading}
          >
            <Statistic
              title="Rutas didácticas"
              value={totals?.rutasDidacticas ?? 0}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* ======== Profesores ======== */}
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        Profesores
      </Typography.Title>
      <Card bordered hoverable style={{ borderRadius: 10, marginBottom: 16 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <Table
            size="middle"
            rowKey={(r) => r._id}
            columns={teacherColumns}
            dataSource={teachers}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        )}
      </Card>

      {/* ======== Alumnos ======== */}
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        Alumnos
      </Typography.Title>
      <Card bordered hoverable style={{ borderRadius: 10 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <List
            size="small"
            dataSource={studentsNames}
            renderItem={(name) => <List.Item>{name}</List.Item>}
            pagination={{ pageSize: 20 }}
          />
        )}
      </Card>
    </div>
  );
}
